import { useState } from "react";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
} from "@dnd-kit/core";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ROLE_LABEL, scopeLabel, WEBFLOW_STATUS } from "@/lib/constants";
import { cn } from "@/lib/utils";

export const TONES = {
  neutral: "border-border bg-secondary text-foreground",
  lilac: "border-lilac bg-lilac text-ink",
  butter: "border-butter-strong bg-butter text-ink",
  mint: "border-mint bg-mint text-ink",
  blush: "border-blush bg-blush text-ink",
  sky: "border-sky bg-sky text-ink",
  peach: "border-peach bg-peach text-ink",
};
export const SECTION_TONE = { Arte: "mint", Moda: "blush", Cultura: "lilac", "Música": "butter", "Fotografía": "sky", Eventos: "peach", "Gastronomía": "butter", Life: "mint", "Tecnología": "sky", Viajes: "peach", "Conoce Now What?": "lilac", Arquitectura: "neutral" };
export const STAGE_TONE = { PUBLISHED: "mint", READY_WEBFLOW: "sky", REVIEW: "butter" };
export const DEAL_TONE = { WON: "mint", PAID: "mint", NEGOTIATION: "butter", PROPOSAL: "lilac" };
export const SCOPE_TONE = { GLOBAL: "sky", LOCAL_SV: "lilac", REGIONAL_CA: "butter" };

export const Pill = ({ children, className, tone = "neutral", ...p }) => (
  <span
    className={cn(
      "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium leading-4 whitespace-nowrap",
      TONES[tone] || TONES.neutral,
      className
    )}
    {...p}
  >
    {children}
  </span>
);

export const Dot = ({ tone = "muted", className }) => (
  <span
    className={cn(
      "inline-block h-1.5 w-1.5 rounded-full",
      tone === "ok" ? "dot-ok" : tone === "warn" ? "dot-warn" : tone === "bad" ? "dot-bad" : "bg-muted-foreground",
      className
    )}
  />
);

export const RoleBadge = ({ role, className }) => (
  <span
    data-testid="role-badge"
    className={cn(
      "inline-flex items-center rounded-full px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider border border-border",
      role === "SUPERADMIN" ? "bg-primary text-primary-foreground border-primary" : "bg-secondary text-foreground",
      className
    )}
  >
    {ROLE_LABEL[role] || role}
  </span>
);

export const SectionTag = ({ section, className }) => <Pill tone={SECTION_TONE[section] || "neutral"} className={className} data-testid="section-tag">{section}</Pill>;
export const StageTag = ({ status, label }) => <Pill tone={STAGE_TONE[status] || "neutral"} data-testid="stage-tag">{label}</Pill>;
export const DealStageTag = ({ stage, label }) => <Pill tone={DEAL_TONE[stage] || "neutral"}>{label}</Pill>;

export const ScopeBadge = ({ scope }) => (
  <Pill tone={SCOPE_TONE[scope]} className="font-mono uppercase tracking-wider text-[10px]">
    {scope === "LOCAL_SV" ? "SV" : scope === "REGIONAL_CA" ? "CA" : "GL"} · {scopeLabel(scope)}
  </Pill>
);

export const WebflowBadge = ({ status }) => (
  <Pill data-testid="webflow-status-badge" tone={status === "LIVE" ? "mint" : status === "DRAFT" ? "sky" : "neutral"}>
    <Dot tone={status === "LIVE" ? "ok" : status === "DRAFT" ? "warn" : "muted"} className={status && status !== "NOT_LINKED" ? "!bg-ink" : ""} />
    {WEBFLOW_STATUS[status || "NOT_LINKED"]}
  </Pill>
);

export const SampleTag = () => (
  <span className="rounded border border-dashed border-border px-1 font-mono text-[9px] uppercase tracking-wider text-muted-foreground">
    muestra
  </span>
);

export const PageHeader = ({ kicker, title, subtitle, actions }) => (
  <div className="flex flex-col gap-4 border-b border-border pb-6 md:flex-row md:items-end md:justify-between">
    <div>
      {kicker && <div className="kicker mb-2">{kicker}</div>}
      <h1 className="font-display text-5xl leading-none tracking-tight md:text-6xl">{title}</h1>
      {subtitle && <p className="mt-3 max-w-2xl text-sm text-muted-foreground">{subtitle}</p>}
    </div>
    {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
  </div>
);

export const EmptyState = ({ title, text, action, testid }) => (
  <div data-testid={testid} className="grain flex flex-col items-center justify-center rounded-lg border border-dashed border-border bg-surface-2 px-6 py-14 text-center">
    <div className="font-display text-3xl">{title}</div>
    {text && <p className="mt-2 max-w-md text-sm text-muted-foreground">{text}</p>}
    {action && <div className="mt-5">{action}</div>}
  </div>
);

export const Splash = ({ label, action }) => (
  <div className="mesh-soft grain flex min-h-screen flex-col items-center justify-center gap-4">
    <div className="font-display text-6xl tracking-tight">Mediana Hub</div>
    <div className="flex items-center gap-2 text-sm text-muted-foreground">
      {!action && <Loader2 className="h-4 w-4 animate-spin" />}
      {label}
    </div>
    {action && (
      <Button variant="outline" onClick={action} data-testid="splash-retry-button">
        Reintentar
      </Button>
    )}
  </div>
);

export const Metric = ({ label, value, hint, testid, children }) => (
  <div data-testid={testid} className="lift flex flex-col justify-between rounded-lg border border-border bg-card p-5">
    <div className="kicker">{label}</div>
    <div className="mt-4 font-display text-5xl leading-none tracking-tight">{value}</div>
    {hint && <div className="mt-2 text-xs text-muted-foreground">{hint}</div>}
    {children}
  </div>
);

/* ---------- Generic Kanban (dnd-kit) ---------- */
function Column({ col, children, count, footer }) {
  const { setNodeRef, isOver } = useDroppable({ id: col.id });
  return (
    <div
      ref={setNodeRef}
      data-testid={`kanban-column-${col.id}`}
      className={cn(
        "flex w-[296px] shrink-0 flex-col rounded-lg border bg-surface-2",
        isOver ? "border-primary border-dashed bg-accent" : "border-border"
      )}
    >
      <div className="flex items-center justify-between border-b border-border px-3 py-2.5">
        <div className="text-xs font-semibold uppercase tracking-wider">{col.label}</div>
        <span className="font-mono text-[11px] text-muted-foreground">{count}</span>
      </div>
      {footer}
      <div className="flex min-h-[120px] flex-1 flex-col gap-2 p-2">{children}</div>
    </div>
  );
}

function DraggableCard({ id, disabled, children }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id, disabled });
  return (
    <div ref={setNodeRef} {...attributes} {...listeners} className={cn(isDragging && "opacity-30", !disabled && "cursor-grab active:cursor-grabbing")}>
      {children}
    </div>
  );
}

export function KanbanBoard({ columns, items, getColumn, onMove, renderCard, canDrag = true, columnFooter }) {
  const [active, setActive] = useState(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }), useSensor(KeyboardSensor));
  return (
    <DndContext
      sensors={sensors}
      onDragStart={(e) => setActive(items.find((i) => i.id === e.active.id))}
      onDragCancel={() => setActive(null)}
      onDragEnd={(e) => {
        setActive(null);
        const item = items.find((i) => i.id === e.active.id);
        if (item && e.over && e.over.id !== getColumn(item)) onMove(item, e.over.id);
      }}
    >
      <div className="scroll-thin flex gap-3 overflow-x-auto pb-4">
        {columns.map((col) => {
          const colItems = items.filter((i) => getColumn(i) === col.id);
          return (
            <Column key={col.id} col={col} count={colItems.length} footer={columnFooter?.(col, colItems)}>
              {colItems.map((it) => (
                <DraggableCard key={it.id} id={it.id} disabled={!canDrag}>
                  {renderCard(it)}
                </DraggableCard>
              ))}
            </Column>
          );
        })}
      </div>
      <DragOverlay>{active ? <div className="rotate-[1.5deg] scale-[1.02] shadow-lg">{renderCard(active)}</div> : null}</DragOverlay>
    </DndContext>
  );
}
